
import { DeleteOutlined, PrinterOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Table,
} from "antd";
import axios from "axios";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ReactToPrint from "react-to-print";
import { API, Headers } from "../../Config";
import "./style.css";

const FormModalReciept = (props) => {
  const Auth = useSelector((state) => state.Auth.user);
  const formRef = useRef();
  const printRef = useRef();
  const [form] = Form.useForm();
  const dateFormat = "DD/MM/YYYY";
  const [disablePagination, setDisablePagination] = useState(false);
  const [receiptType, setReceiptType] = useState(
    props.mode === "put" && props.item?.voucherDetails?.[0]?.sales_id
      ? "Sales Receipt"
      : props.mode === "put"
      ? "Other Receipt"
      : "Sales Receipt"
  );
  const [isUpdateDisabled, setIsUpdateDisabled] = useState(false);

  // Initialize dataSource based on mode and receipt type
  let initialData = [];
  let initialFormList = {};
  if (props.mode === "post") {
    initialData = [
      {
        key: 1,
        account_no: null,
        description: "",
        ...(receiptType === "Sales Receipt"
          ? {
              total_amount: 0,
              paid_amount: 0,
              paid_cr_amount: 0,
              pay_amount: 0,
              sales_id: null,
              outstanding: 0,
              disabled: false,
            }
          : { amount_cr: 0, amount_dr: 0 }),
      },
    ];
    initialFormList = {
      1: {
        account_no: null,
        description: "",
        ...(receiptType === "Sales Receipt"
          ? {
              total_amount: 0,
              paid_amount: 0,
              paid_cr_amount: 0,
              pay_amount: 0,
              sales_id: null,
            }
          : { amount_cr: 0, amount_dr: 0 }),
      },
    };
  } else if (props.mode === "put" && props.item?.id) {
    initialData = props.item.voucherDetails.map((item) => ({
      key: item.key.toString(),
      account_no: props.item.voucherDetails[0].account_no, // Use first account_no for consistency
      description: item.description,
      ...(receiptType === "Sales Receipt"
        ? {
            total_amount: Number(item.amount_dr || 0),
            paid_amount: Number(item.amount_cr || 0),
            paid_cr_amount: Number(item.amount_cr || 0),
            pay_amount: 0,
            sales_id: item.sales_id,
            outstanding:
              Number(item.amount_dr || 0) - Number(item.amount_cr || 0),
            disabled:
              Number(item.amount_dr || 0) - Number(item.amount_cr || 0) <= 0,
          }
        : {
            amount_cr: Number(item.amount_cr || 0),
            amount_dr: Number(item.amount_dr || 0),
          }),
    }));
    initialFormList = initialData.reduce((acc, item) => {
      acc[item.key] = {
        account_no: props.item.voucherDetails[0].account_no,
        description: item.description,
        ...(receiptType === "Sales Receipt"
          ? {
              total_amount: item.total_amount,
              paid_amount: item.paid_amount,
              paid_cr_amount: item.paid_cr_amount,
              pay_amount: item.pay_amount,
              sales_id: item.sales_id,
            }
          : {
              amount_cr: item.amount_cr,
              amount_dr: item.amount_dr,
            }),
      };
      return acc;
    }, {});
  }

  const [dataSource, setDataSource] = useState(initialData);
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [totalPay, setTotalPay] = useState(0);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [totalCr, setTotalCr] = useState(0);
  const [totalDr, setTotalDr] = useState(0);

  const tableRef = useRef(null);

  // Calculate totals for Sales Receipt
  const calculateSalesTotals = (data) => {
    const totalAmt = data.reduce(
      (acc, item) => acc + Number(item.total_amount || 0),
      0
    );
    const totalPd = data.reduce(
      (acc, item) => acc + Number(item.paid_amount || 0),
      0
    );
    const totalPy = data.reduce(
      (acc, item) => acc + Number(item.pay_amount || 0),
      0
    );
    const totalOut = data.reduce(
      (acc, item) =>
        acc + (Number(item.total_amount || 0) - Number(item.paid_amount || 0)),
      0
    );
    setTotalAmount(totalAmt);
    setTotalPaid(totalPd);
    setTotalPay(totalPy);
    setTotalOutstanding(totalOut);
    form.setFieldsValue({
      total_amount: totalAmt,
      total_paid: totalPd,
      total_pay: totalPy,
      total_outstanding: totalOut,
    });
  };

  // Calculate totals for Other Receipt
  const calculateOtherTotals = (data) => {
    const totalCr = data.reduce(
      (acc, item) => acc + Number(item.amount_cr || 0),
      0
    );
    setTotalCr(totalCr);
    setTotalDr(0);
    form.setFieldsValue({
      cr_total: totalCr,
      dr_total: 0,
    });
  };

  // Initial data fetch on mount
  useEffect(() => {
    fetchData(page, take);
    if (props.mode === "put") {
      if (receiptType === "Sales Receipt") {
        calculateSalesTotals(initialData);
      } else {
        calculateOtherTotals(initialData);
      }
      form.setFieldsValue({
        list: initialFormList,
        to_account: props.item?.voucherDetails[0]?.account_no,
      });
    } else {
      if (receiptType === "Sales Receipt") {
        calculateSalesTotals(initialData);
      } else {
        calculateOtherTotals(initialData);
      }
      form.setFieldsValue({
        list: initialFormList,
      });
    }
  }, [receiptType, props.mode]);

  // Recalculate totals and check update button status whenever dataSource changes
  useEffect(() => {
    if (receiptType === "Sales Receipt") {
      calculateSalesTotals(dataSource);
      if (props.mode === "put") {
        const hasValidRow = dataSource.some(
          (item) =>
            Number(item.pay_amount || 0) > 0 &&
            Number(item.outstanding || 0) > 0
        );
        setIsUpdateDisabled(!hasValidRow);
      }
    } else {
      calculateOtherTotals(dataSource);
      if (props.mode === "put") {
        const hasValidRow = dataSource.some(
          (item) => Number(item.amount_cr || 0) > 0
        );
        setIsUpdateDisabled(!hasValidRow);
      }
    }
  }, [dataSource, props.mode, receiptType]);

  const onFinishFailed = (errorInfo) => {};

  const onFinish = (values) => {
    setLoading(true);
    const fromAccountNo = values.account_no;
    const toAccountNo = values.to_account;
    let obj = {
      voucher_date: values.voucher_date,
      voucher_type: props.page_mode,
      account_no: fromAccountNo,
      description: values.description,
      userid: Number(Auth.id),
      ...(receiptType === "Sales Receipt"
        ? {
            cr_total: Number(totalPay) + Number(totalPaid),
            totalpaidd: Number(totalPaid),
            dr_total: Number(totalAmount),
            voucherItems:
              props.mode === "put"
                ? dataSource
                    .filter(
                      (item) =>
                        Number(item.pay_amount || 0) > 0 &&
                        Number(item.outstanding || 0) > 0
                    )
                    .map((item) => ({
                      key: item.key,
                      account_no: toAccountNo,
                      description: item.description,
                      amount_cr: Number(item.pay_amount || 0),
                      paid_cr_amount: Number(item.paid_cr_amount || 0),
                      amount_dr: item.total_amount,
                      sales_id: item.sales_id,
                    }))
                : dataSource
                    .filter((item) => !item.disabled)
                    .map((item) => ({
                      key: item.key,
                      account_no: toAccountNo,
                      description: item.description,
                      amount_cr: Number(item.pay_amount || 0),
                      paid_cr_amount: Number(item.paid_cr_amount || 0),
                      amount_dr: item.total_amount,
                      sales_id: item.sales_id,
                    })),
          }
        : {
            cr_total: Number(totalCr),
            dr_total: 0,
            voucherItems:
              props.mode === "put"
                ? dataSource
                    .filter((item) => Number(item.amount_cr || 0) > 0)
                    .map((item) => ({
                      key: item.key,
                      account_no: item.account_no,
                      description: item.description,
                      amount_cr: item.amount_cr,
                      amount_dr: item.amount_dr,
                    }))
                : dataSource.map((item) => ({
                    key: item.key,
                    account_no: item.account_no,
                    description: item.description,
                    amount_cr: item.amount_cr,
                    amount_dr: item.amount_dr,
                  })),
          }),
    };

    if (props.mode === "put" && obj.voucherItems.length === 0) {
      setLoading(false);
      message.error("No valid data to update.");
      return;
    }

    let id = props?.item?.id;
    let URL =
      props.mode === "post"
        ? API.VOUCHER_ACTIONS_RECEIPT + props.page_mode
        : API.VOUCHER_ACTIONS_RECEIPT + id;
    axios({
      method: props.mode,
      url: API.BASR_URL + URL,
      headers: Headers(),
      data: obj,
    })
      .then(function (response) {
        setLoading(false);
        if ([200, 201].includes(response.status)) {
          message.success("Success");
          props.onchange();
        }
      })
      .catch(function (error) {
        setLoading(false);
        message.error("Oops. Something went wrong while saving.");
      });
  };

  const handleAddProduct = () => {
    const newDataSource = [...dataSource];
    const newKey =
      Math.max(...dataSource.map((item) => Number(item.key)), 0) + 1;
    newDataSource.push({
      key: newKey.toString(),
      account_no: form.getFieldValue("to_account"),
      description: "",
      ...(receiptType === "Sales Receipt"
        ? {
            total_amount: 0,
            paid_amount: 0,
            paid_cr_amount: 0,
            pay_amount: 0,
            sales_id: null,
            outstanding: 0,
            disabled: false,
          }
        : { amount_cr: 0, amount_dr: 0 }),
    });
    setDataSource(newDataSource);
    form.setFieldsValue({
      list: {
        ...form.getFieldValue("list"),
        [newKey]: {
          account_no: form.getFieldValue("to_account"),
          description: "",
          ...(receiptType === "Sales Receipt"
            ? {
                total_amount: 0,
                paid_amount: 0,
                paid_cr_amount: 0,
                pay_amount: 0,
                sales_id: null,
              }
            : { amount_cr: 0, amount_dr: 0 }),
        },
      },
    });
  };

  const handleRemoveProduct = (key) => {
    let deleteList = [...dataSource];
    let keys = deleteList.findIndex((value) => value.key === key);
    deleteList.splice(keys, 1);
    setDataSource(deleteList);
    const currentList = form.getFieldValue("list") || {};
    delete currentList[key];
    form.setFieldsValue({ list: currentList });
  };

  const handleChangeValueSet = (value, key, field) => {
    const newDataSource = [...dataSource];
    newDataSource.forEach((val) => {
      if (val.key === key) {
        if (receiptType === "Sales Receipt" && field === "pay_amount") {
          const maxPay =
            Number(val.total_amount || 0) - Number(val.paid_amount || 0);
          val[field] = value > maxPay ? maxPay : value < 0 ? 0 : value;
        } else if (receiptType === "Other Receipt" && field === "amount_dr") {
          val[field] = 0;
        } else {
          val[field] = value;
        }
        if (receiptType === "Sales Receipt") {
          val.outstanding =
            Number(val.total_amount || 0) - Number(val.paid_amount || 0);
          val.disabled = val.outstanding <= 0;
        }
      }
    });
    setDataSource(newDataSource);
    const currentList = form.getFieldValue("list") || {};
    currentList[key] = {
      ...currentList[key],
      [field]: value,
    };
    form.setFieldsValue({ list: currentList });
  };

  const handleToAccountChange = (value) => {
    if (receiptType === "Sales Receipt") {
      const newDataSource = dataSource.map((item) => ({
        ...item,
        account_no: value,
      }));
      setDataSource(newDataSource);
      const newList = {};
      newDataSource.forEach((item) => {
        newList[item.key] = {
          ...form.getFieldValue("list")[item.key],
          account_no: value,
        };
      });
      form.setFieldsValue({ list: newList });
    }
  };

  const fetchData = async (p, t) => {
    try {
      const response = await axios.get(
        API.BASR_URL + API.AWB_PAYMENT_LIST + `?order=DESC&page=${p}&take=${t}`,
        { headers: Headers() }
      );
      if (response.status === 200) {
        setPaymentData(response.data?.data);
      }
    } catch (error) {
      message.error("Oops. Something went wrong");
    }
  };

  const fetchAllDataByAccount = async (id) => {
    if (receiptType !== "Sales Receipt" || props.mode === "put") return;
    try {
      const response = await axios.get(
        API.BASR_URL + API.SALE + "/byAccount/" + id,
        { headers: Headers() }
      );
      if (response.status === 200) {
        const res = response.data?.data || [];
        const newDataSource = res.flatMap((item) =>
          item.saleDetails.map((detail) => {
            const totalAmount = Number(detail.total || 0);
            const paidAmount = Number(detail.paid_amount || 0);
            const outstanding = totalAmount - paidAmount;
            return {
              key: detail.id.toString(),
              account_no: form.getFieldValue("to_account"),
              description: `${detail.name} - ${detail.description}`.trim(),
              total_amount: totalAmount,
              paid_amount: paidAmount,
              paid_cr_amount: paidAmount,
              pay_amount: 0,
              sales_id: detail.sales_id,
              outstanding: outstanding,
              disabled: outstanding <= 0,
            };
          })
        );
        setDataSource(newDataSource);
        const newList = {};
        newDataSource.forEach((item) => {
          newList[item.key] = {
            account_no: form.getFieldValue("to_account"),
            description: item.description,
            total_amount: item.total_amount,
            paid_amount: item.paid_amount,
            paid_cr_amount: item.paid_cr_amount,
            pay_amount: item.pay_amount,
            sales_id: item.sales_id,
          };
        });
        form.setFieldsValue({ list: newList });
        calculateSalesTotals(newDataSource);
      }
    } catch (error) {
      message.error("Oops. Something went wrong");
      setDataSource(initialData);
      form.setFieldsValue({ list: initialFormList });
    }
  };

  const getPrintColumns = () => {
    if (receiptType === "Sales Receipt") {
      return [
        {
          title: "#",
          dataIndex: "slno",
          key: "slno",
          width: "30px",
          align: "center",
          render: (text, record, index) => index + 1,
        },
        {
          title: "Description",
          dataIndex: "description",
          key: "description",
          width: "250px",
        },
        {
          title: "Total Amount",
          dataIndex: "total_amount",
          key: "total_amount",
          width: "100px",
          align: "right",
          render: (text) => Number(text || 0).toFixed(2),
        },
        {
          title: "Paid Amount",
          dataIndex: "paid_amount",
          key: "paid_amount",
          width: "100px",
          align: "right",
          render: (text) => Number(text || 0).toFixed(2),
        },
        {
          title: "Pay Amount",
          dataIndex: "pay_amount",
          key: "pay_amount",
          width: "100px",
          align: "right",
          render: (text) => Number(text || 0).toFixed(2),
        },
        {
          title: "Outstanding",
          dataIndex: "outstanding",
          key: "outstanding",
          width: "100px",
          align: "right",
          render: (text) => Number(text || 0).toFixed(2),
        },
      ];
    } else {
      return [
        {
          title: "#",
          dataIndex: "slno",
          key: "slno",
          width: "30px",
          align: "center",
          render: (text, record, index) => index + 1,
        },
        {
          title: "To Account",
          dataIndex: "account_no",
          key: "account_no",
          width: "200px",
          render: (text) => {
            const account = props.accounts.find(
              (acc) => acc.account_no === text
            );
            return account
              ? `${account.account_no} - ${account.account_name}`
              : text || "-";
          },
        },
        {
          title: "Description",
          dataIndex: "description",
          key: "description",
          width: "250px",
        },
        {
          title: "Amount",
          dataIndex: "amount_cr",
          key: "amount_cr",
          width: "100px",
          align: "right",
          render: (text) => Number(text || 0).toFixed(2),
        },
      ];
    }
  };

  const columns = [
    {
      title: "#",
      dataIndex: "slno",
      key: "slno",
      width: "30px",
      align: "center",
      visible: true,
      fixed: "left",
      render: (text, record, index) => index + 1,
    },
    ...(receiptType === "Sales Receipt"
      ? []
      : [
          {
            title: "To Account",
            dataIndex: "account_no",
            key: "account_no",
            visible: true,
            width: "300px",
            fixed: "left",
            render: (text, record) => (
              <Form.Item
                style={{ padding: 0, margin: 0 }}
                name={["list", record.key, "account_no"]}
                initialValue={props.mode === "put" ? record.account_no : null}
                rules={[{ required: true, message: "required" }]}
              >
                <Select
                  bordered={false}
                  className="CustomerForm-Select"
                  showSearch
                  allowClear
                  placeholder="Select account"
                  filterOption={(input, option) => {
                    const accountNo = option.value.toLowerCase();
                    const accountName = option.children
                      .toString()
                      .toLowerCase();
                    return (
                      accountNo.includes(input.toLowerCase()) ||
                      accountName.includes(input.toLowerCase())
                    );
                  }}
                  style={{
                    width: "100%",
                    backgroundColor: "white",
                    borderRadius: "0px",
                  }}
                  onChange={(value) => {
                    handleChangeValueSet(value, record.key, "account_no");
                  }}
                >
                  {props.accounts.map((item) => (
                    <Select.Option
                      key={item.account_no}
                      value={item.account_no}
                    >
                      {item.account_no} - {item.account_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            ),
          },
        ]),
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      visible: true,
      width: "250px",
      render: (text, record) => (
        <Form.Item
          style={{ padding: 0, margin: 0 }}
          name={["list", record.key, "description"]}
          initialValue={text}
          rules={[{ required: true, message: "required" }]}
        >
          <Input.TextArea
            style={{ width: "100%" }}
            autoSize={{ minRows: 1, maxRows: 6 }}
            bordered={false}
            disabled={receiptType === "Sales Receipt" && record.disabled}
            onChange={(e) =>
              handleChangeValueSet(e.target.value, record.key, "description")
            }
          />
        </Form.Item>
      ),
    },
    ...(receiptType === "Sales Receipt"
      ? [
          {
            title: "Total Amount",
            dataIndex: "total_amount",
            key: "total_amount",
            visible: true,
            width: "150px",
            render: (text, record) => (
              <Form.Item
                style={{ padding: 0, margin: 0 }}
                name={["list", record.key, "total_amount"]}
                initialValue={text}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  bordered={false}
                  disabled
                  value={Number(text || 0).toFixed(2)}
                />
              </Form.Item>
            ),
          },
          {
            title: "Paid Amount",
            dataIndex: "paid_amount",
            key: "paid_amount",
            visible: true,
            width: "150px",
            render: (text, record) => (
              <Form.Item
                style={{ padding: 0, margin: 0 }}
                name={["list", record.key, "paid_amount"]}
                initialValue={text}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  bordered={false}
                  disabled
                  value={Number(text || 0).toFixed(2)}
                />
              </Form.Item>
            ),
          },
          {
            title: "Pay Amount",
            dataIndex: "pay_amount",
            key: "pay_amount",
            visible: true,
            width: "150px",
            render: (text, record) => (
              <Form.Item
                style={{ padding: 0, margin: 0 }}
                name={["list", record.key, "pay_amount"]}
                initialValue={text}
                rules={[
                  {
                    validator: (_, value) => {
                      const maxPay =
                        Number(record.total_amount || 0) -
                        Number(record.paid_amount || 0);
                      if (value > maxPay) {
                        return Promise.reject(
                          `Cannot exceed ${maxPay.toFixed(2)}`
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  bordered={false}
                  min={0}
                  disabled={record.disabled}
                  onChange={(value) =>
                    handleChangeValueSet(value, record.key, "pay_amount")
                  }
                />
              </Form.Item>
            ),
          },
          {
            title: "Outstanding",
            dataIndex: "outstanding",
            key: "outstanding",
            visible: true,
            width: "150px",
            render: (text, record) => (
              <Form.Item
                style={{ padding: 0, margin: 0 }}
                name={["list", record.key, "outstanding"]}
                initialValue={
                  Number(record.total_amount || 0) -
                  Number(record.paid_amount || 0)
                }
              >
                <InputNumber
                  style={{ width: "100%" }}
                  bordered={false}
                  disabled
                  value={Number(
                    Number(record.total_amount || 0) -
                      Number(record.paid_amount || 0)
                  ).toFixed(2)}
                />
              </Form.Item>
            ),
          },
        ]
      : [
          {
            title: "Amount",
            dataIndex: "amount_cr",
            key: "amount_cr",
            visible: true,
            width: "150px",
            render: (text, record) => (
              <Form.Item
                style={{ padding: 0, margin: 0 }}
                name={["list", record.key, "amount_cr"]}
                initialValue={text}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  bordered={false}
                  onChange={(value) =>
                    handleChangeValueSet(value, record.key, "amount_cr")
                  }
                />
              </Form.Item>
            ),
          },
        ]),
    {
      title: " ",
      key: "action",
      visible: true,
      align: "center",
      width: "30px",
      fixed: "right",
      render: (text, record) => (
        <Space size="middle">
          <DeleteOutlined
            onClick={() => {
              if (dataSource.length > 1) {
                handleRemoveProduct(record.key);
              }
            }}
          />
        </Space>
      ),
    },
  ];

  const renderSummary = () => {
    if (receiptType === "Sales Receipt") {
      return (
        <Table.Summary.Row
          className="my-summary-row"
          style={{ height: "50px", alignItems: "left" }}
        >
          <Table.Summary.Cell className="cell" fixed="left">
            {/* <Button
              style={{
                textDecoration: "underline",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
              size="small"
              bordered={false}
              onClick={handleAddProduct}
            >
              Add New
            </Button> */}
          </Table.Summary.Cell>
          <Table.Summary.Cell className="cell">
            <b>Total</b>
          </Table.Summary.Cell>
          <Table.Summary.Cell className="cell" align="center">
            <Form.Item name="total_amount" style={{ padding: 0, margin: 0 }}>
              <b style={{ paddingLeft: "9px", textAlign: "left" }}>
                {Number(totalAmount).toFixed(2)}
              </b>
            </Form.Item>
          </Table.Summary.Cell>
          <Table.Summary.Cell className="cell" align="center">
            <Form.Item name="total_paid" style={{ padding: 0, margin: 0 }}>
              <b style={{ paddingLeft: "9px", textAlign: "left" }}>
                {Number(totalPaid).toFixed(2)}
              </b>
            </Form.Item>
          </Table.Summary.Cell>
          <Table.Summary.Cell className="cell" align="center">
            <Form.Item name="total_pay" style={{ padding: 0, margin: 0 }}>
              <b style={{ paddingLeft: "9px", textAlign: "left" }}>
                {Number(totalPay).toFixed(2)}
              </b>
            </Form.Item>
          </Table.Summary.Cell>
          <Table.Summary.Cell className="cell" align="center">
            <Form.Item
              name="total_outstanding"
              style={{ padding: 0, margin: 0 }}
            >
              <b style={{ paddingLeft: "9px", textAlign: "left" }}>
                {Number(totalOutstanding).toFixed(2)}
              </b>
            </Form.Item>
          </Table.Summary.Cell>
          <Table.Summary.Cell
            className="cell"
            fixed="right"
          ></Table.Summary.Cell>
        </Table.Summary.Row>
      );
    } else {
      return (
        <Table.Summary.Row
          className="my-summary-row"
          style={{ height: "50px", alignItems: "left" }}
        >
          <Table.Summary.Cell
            className="cell"
            fixed="left"
          ></Table.Summary.Cell>
          <Table.Summary.Cell className="cell" fixed="left">
            <Button
              style={{
                textDecoration: "underline",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
              size="small"
              bordered={false}
              onClick={handleAddProduct}
            >
              Add New
            </Button>
          </Table.Summary.Cell>
          <Table.Summary.Cell className="cell">
            <b>Total</b>
          </Table.Summary.Cell>
          <Table.Summary.Cell className="cell" align="center">
            <Form.Item name="cr_total" style={{ padding: 0, margin: 0 }}>
              <b style={{ paddingLeft: "9px", textAlign: "left" }}>
                {Number(totalCr).toFixed(2)}
              </b>
            </Form.Item>
          </Table.Summary.Cell>
          <Table.Summary.Cell
            className="cell"
            fixed="right"
          ></Table.Summary.Cell>
        </Table.Summary.Row>
      );
    }
  };

  const renderPrintSummary = () => {
    if (receiptType === "Sales Receipt") {
      return (
        <Table.Summary.Row className="my-summary-row">
          <Table.Summary.Cell className="cell"></Table.Summary.Cell>
          <Table.Summary.Cell className="cell">
            <b>Total</b>
          </Table.Summary.Cell>
          <Table.Summary.Cell className="cell" align="right">
            <b>{Number(totalAmount).toFixed(2)}</b>
          </Table.Summary.Cell>
          <Table.Summary.Cell className="cell" align="right">
            <b>{Number(totalPaid).toFixed(2)}</b>
          </Table.Summary.Cell>
          <Table.Summary.Cell className="cell" align="right">
            <b>{Number(totalPay).toFixed(2)}</b>
          </Table.Summary.Cell>
          <Table.Summary.Cell className="cell" align="right">
            <b>{Number(totalOutstanding).toFixed(2)}</b>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      );
    } else {
      return (
        <Table.Summary.Row className="my-summary-row">
          <Table.Summary.Cell className="cell"></Table.Summary.Cell>
          <Table.Summary.Cell className="cell"></Table.Summary.Cell>
          <Table.Summary.Cell className="cell">
            <b>Total</b>
          </Table.Summary.Cell>
          <Table.Summary.Cell className="cell" align="right">
            <b>{Number(totalCr).toFixed(2)}</b>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      );
    }
  };

  const getFormValues = () => {
    const values = form.getFieldsValue();
    return values;
  };

  const getAccountDetails = (accountNo) => {
    const account = props.accounts.find((acc) => acc.account_no === accountNo);
    return account
      ? `${account.account_no} - ${account.account_name}`
      : accountNo || "-";
  };

  const handleReceiptTypeChange = (value) => {
    if (props.mode === "put") return;
    setReceiptType(value);
    const newData = [
      {
        key: 1,
        account_no: null,
        description: "",
        ...(value === "Sales Receipt"
          ? {
              total_amount: 0,
              paid_amount: 0,
              paid_cr_amount: 0,
              pay_amount: 0,
              sales_id: null,
              outstanding: 0,
              disabled: false,
            }
          : { amount_cr: 0, amount_dr: 0 }),
      },
    ];
    setDataSource(newData);
    form.setFieldsValue({
      list: {
        1: {
          account_no: null,
          description: "",
          ...(value === "Sales Receipt"
            ? {
                total_amount: 0,
                paid_amount: 0,
                paid_cr_amount: 0,
                pay_amount: 0,
                sales_id: null,
              }
            : { amount_cr: 0, amount_dr: 0 }),
        },
      },
      to_account: null,
    });
  };

  return (
    <div>
      <Modal
        title={props.columnsVisible.page}
        centered
        open={props.visible}
        onOk={() => props.onClose()}
        onCancel={() => props.onClose()}
        width={receiptType === "Sales Receipt" ? 1200 : 1000}
        footer={false}
        style={{ borderRadius: 10 }}
      >
        <Form
          form={form}
          ref={formRef}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={
            props.mode === "post"
              ? {
                  voucher_date: moment(),
                  amount: 0.0,
                  currency_id: props.defultcurrency,
                  receipt_type: "Sales Receipt",
                  ...(receiptType === "Sales Receipt"
                    ? {
                        total_amount: 0,
                        total_paid: 0,
                        total_pay: 0,
                        total_outstanding: 0,
                      }
                    : {
                        cr_total: 0,
                        dr_total: 0,
                      }),
                  list: initialFormList,
                }
              : {
                  voucher_date: props.item && moment(props.item.voucher_date),
                  account_no: props.item && props.item.account_no,
                  to_account: props.item?.voucherDetails[0]?.account_no,
                  description: props.item && props.item.description,
                  receipt_type: receiptType,
                  ...(receiptType === "Sales Receipt"
                    ? {
                        total_amount: initialData.reduce(
                          (acc, item) => acc + Number(item.total_amount || 0),
                          0
                        ),
                        total_paid: initialData.reduce(
                          (acc, item) => acc + Number(item.paid_amount || 0),
                          0
                        ),
                        total_pay: initialData.reduce(
                          (acc, item) => acc + Number(item.pay_amount || 0),
                          0
                        ),
                        total_outstanding: initialData.reduce(
                          (acc, item) =>
                            acc +
                            (Number(item.total_amount || 0) -
                              Number(item.paid_amount || 0)),
                          0
                        ),
                      }
                    : {
                        cr_total: initialData.reduce(
                          (acc, item) => acc + Number(item.amount_cr || 0),
                          0
                        ),
                        dr_total: 0,
                      }),
                  list: initialFormList,
                }
          }
          hideRequiredMark
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              {props.page_mode === "JV" ? null : (
                <Form.Item
                  style={{ marginBottom: "0px" }}
                  labelCol={{ span: 7 }}
                  wrapperCol={{ span: 16 }}
                  name="account_no"
                  label="Supplier"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Select
                    size="small"
                    placeholder="Select supplier"
                    showSearch
                    allowClear
                    style={{ borderRadius: "0px" }}
                    filterOption={(input, option) => {
                      const accountNo = option.value.toLowerCase();
                      const accountName = option.children
                        .toString()
                        .toLowerCase();
                      return (
                        accountNo.includes(input.toLowerCase()) ||
                        accountName.includes(input.toLowerCase())
                      );
                    }}
                    onChange={(value) => {
                      if (
                        value &&
                        props.mode === "post" &&
                        receiptType === "Sales Receipt"
                      ) {
                        fetchAllDataByAccount(value);
                      }
                    }}
                  >
                    {props.accounts.map((item) => (
                      <Select.Option
                        key={item.account_no}
                        value={item.account_no}
                      >
                        {item.account_no} - {item.account_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              {receiptType === "Sales Receipt" && (
                <Form.Item
                  style={{ marginBottom: "0px" }}
                  labelCol={{ span: 7 }}
                  wrapperCol={{ span: 16 }}
                  name="to_account"
                  label="Account"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Select
                    size="small"
                    placeholder="Select account"
                    showSearch
                    allowClear
                    style={{ borderRadius: "0px" }}
                    onChange={handleToAccountChange}
                    filterOption={(input, option) => {
                      const accountNo = option.value.toLowerCase();
                      const accountName = option.children
                        .toString()
                        .toLowerCase();
                      return (
                        accountNo.includes(input.toLowerCase()) ||
                        accountName.includes(input.toLowerCase())
                      );
                    }}
                  >
                    {props.accounts.map((item) => (
                      <Select.Option
                        key={item.account_no}
                        value={item.account_no}
                      >
                        {item.account_no} - {item.account_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              <Form.Item
                style={{ marginBottom: "0px" }}
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 16 }}
                name="receipt_type"
                label="Receipt Type"
                initialValue={receiptType}
              >
                <Select
                  size="small"
                  onChange={handleReceiptTypeChange}
                  style={{ borderRadius: "0px" }}
                  disabled={props.mode === "put"}
                >
                  <Select.Option value="Sales Receipt">
                    Sales Receipt
                  </Select.Option>
                  <Select.Option value="Other Receipt">
                    Other Receipt
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                style={{ marginBottom: "0px" }}
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 7 }}
                name="voucher_date"
                label="Voucher Date"
                rules={[{ required: true, message: "required" }]}
              >
                <DatePicker
                  size="small"
                  format={dateFormat}
                  placeholder="Pick a date"
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                style={{ marginBottom: "12px" }}
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 16 }}
                name="description"
                label="Description"
                rules={[{ required: true, message: "required" }]}
              >
                <Input.TextArea
                  required={true}
                  rows={2}
                  size="small"
                  placeholder="Enter Remarks"
                />
              </Form.Item>
            </Col>
          </Row>
          <Table
            className="my-table"
            dataSource={dataSource}
            columns={columns.filter((column) => column.visible)}
            pagination={false}
            size="small"
            bordered
            summary={renderSummary}
            rowKey="key"
            ref={tableRef}
          />
          <Row justify="end">
            <Col sm={4} xs={8} style={{ padding: "10px" }}>
              <ReactToPrint
                trigger={() => (
                  <Button icon={<PrinterOutlined />} block>
                    Print
                  </Button>
                )}
                content={() => printRef.current}
                onBeforeGetContent={() => {
                  setDisablePagination(true);
                  return Promise.resolve();
                }}
                onAfterPrint={() => {
                  setDisablePagination(false);
                }}
              />
            </Col>
            <Col sm={4} xs={8} style={{ padding: "10px" }}>
              <Button onClick={() => props.onClose()} block>
                Cancel
              </Button>
            </Col>
            <Col sm={4} xs={8} style={{ padding: "10px" }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                disabled={props.mode === "put" && isUpdateDisabled}
              >
                {props.mode === "post" ? "Save" : "Update"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>

      <div style={{ display: "none" }}>
        <div
          ref={printRef}
          style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}
        >
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <h2>{props.columnsVisible.page || "Voucher"}</h2>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div>
                  <strong>Date:</strong>{" "}
                  {form.getFieldValue("voucher_date")
                    ? moment(form.getFieldValue("voucher_date")).format(
                        "DD/MM/YYYY"
                      )
                    : ""}
                </div>
                {props.page_mode !== "JV" && (
                  <>
                    <div>
                      <strong>From Account:</strong>{" "}
                      {getAccountDetails(form.getFieldValue("account_no"))}
                    </div>
                    {receiptType === "Sales Receipt" && (
                      <div>
                        <strong>To Account:</strong>{" "}
                        {getAccountDetails(form.getFieldValue("to_account"))}
                      </div>
                    )}
                  </>
                )}
              </Col>
              <Col span={12}>
                <div>
                  <strong>Description:</strong>{" "}
                  {form.getFieldValue("description") || ""}
                </div>
              </Col>
            </Row>
          </div>

          <Table
            size="small"
            columns={getPrintColumns()}
            dataSource={dataSource}
            pagination={false}
            bordered
            summary={renderPrintSummary}
            rowKey="key"
          />

          <div style={{ marginTop: "20px", textAlign: "right" }}>
            {receiptType === "Sales Receipt" ? (
              <>
                <div>
                  <strong>
                    Total Amount: {Number(totalAmount).toFixed(2)}
                  </strong>
                </div>
                <div>
                  <strong>Total Paid: {Number(totalPaid).toFixed(2)}</strong>
                </div>
                <div>
                  <strong>Total Pay: {Number(totalPay).toFixed(2)}</strong>
                </div>
                <div>
                  <strong>
                    Total Outstanding: {Number(totalOutstanding).toFixed(2)}
                  </strong>
                </div>
              </>
            ) : (
              <div>
                <strong>Total Amount: {Number(totalCr).toFixed(2)}</strong>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormModalReciept;
